import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import RfqWorkspaceMock from "./RfqWorkspaceMock";
import { WORKSPACE_URL } from "../../constants/links";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 sm:pt-36 pb-12 sm:pb-16 overflow-hidden"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-grid pointer-events-none" />
      <div className="absolute inset-0 bg-noise opacity-[0.4] pointer-events-none" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[1100px] rounded-[100%] bg-gradient-to-b from-[#0044FF]/8 to-transparent blur-2xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Eyebrow row */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center gap-3 mb-6"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-black/[0.08] bg-white/70 backdrop-blur px-3 py-1.5">
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            <span className="eyebrow">Workspace Product</span>
            <span className="text-black/30">/</span>
            <span className="font-mono text-[11px] tracking-[0.18em] text-black/55">
              SOURCING REQUEST MANAGEMENT
            </span>
          </span>
        </motion.div>

        {/* Headline */}
        <div className="text-center max-w-5xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display font-black tracking-[-0.045em] leading-[0.95] text-[64px] sm:text-[88px] lg:text-[112px] text-[#0A0A0A]"
            data-testid="hero-headline"
          >
            RFQ
            <span className="align-super text-[0.35em] font-bold text-black/40 ml-1">™</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15 }}
            className="mt-6 font-display text-[22px] sm:text-[30px] lg:text-[36px] tracking-[-0.02em] leading-[1.1] font-semibold text-[#0A0A0A]"
            data-testid="hero-subheadline"
          >
            Start Every Import Operation
            <br />
            <span className="text-[#0044FF]">With A Professional RFQ.</span>
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.25 }}
            className="mt-6 mx-auto max-w-2xl text-[15px] sm:text-base leading-relaxed text-black/60"
            data-testid="hero-description"
          >
            Create one structured sourcing request, define every commercial and
            technical requirement, and send it to verified Turkish manufacturers
            inside one Workspace.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <a
              data-testid="hero-create-rfq-btn"
              href={WORKSPACE_URL}
              className="btn-primary inline-flex items-center gap-2 text-[14px]"
            >
              Create Your RFQ™
              <ArrowUpRight className="h-4 w-4" strokeWidth={2.25} />
            </a>
            <a
              data-testid="hero-create-workspace-btn"
              href={WORKSPACE_URL}
              className="btn-secondary inline-flex items-center gap-2 text-[14px]"
            >
              <Sparkles className="h-4 w-4" strokeWidth={2} />
              Create Your Free Workspace
            </a>
          </motion.div>

        </div>

        {/* Workspace Mock */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 sm:mt-20"
        >
          <RfqWorkspaceMock />
        </motion.div>
      </div>
    </section>
  );
}
